import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getStudyProcedures } from 'api/study';
import { compact, find, flatMap, isEmpty, last, size, some, times } from 'lodash';
import { useEffect, useState } from 'react';
import queryClient from 'utils/queryClient';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';

import { selectCompleteCachedStudyProcedures } from './completeCachedStudyProcedures';

const PAGE_SIZE = 50;
const MAX_CONCURRENT_QUERIES = 5;

export const useAllStudyProcedures = (studyId: string, enabled: boolean = true) => {
  const { labId } = useCurrentLabId();
  const activeQueryClient = useQueryClient();
  const {
    data: studies,
    isError: isStatisticsError,
    isLoading: isStatisticsLoading,
    isFetching: isStatisticsFetching,
  } = useStudies(labId, true, { enabled });
  const study = find(studies, { id: studyId });
  // The study statistics count only the cases the user may see in the main case list, while the study procedures
  // endpoint returns every case. The count is therefore only a hint for how many pages to request in parallel.
  const hintedPages = Math.max(1, Math.ceil((study?.casesCount ?? 0) / PAGE_SIZE));
  const [extraPages, setExtraPages] = useState({ studyId, count: 0 });
  const totalPages = hintedPages + (extraPages.studyId === studyId ? extraPages.count : 0);

  const proceduresQueries = useQueries({
    queries: times(study ? totalPages : 0, (index) => {
      return {
        queryKey: ['procedures', studyId, index + 1],
        queryFn: () => getStudyProcedures({ studyId, page: index + 1, pageSize: PAGE_SIZE }),
        enabled: enabled && Boolean(studyId),
        config: {
          concurrency: MAX_CONCURRENT_QUERIES,
        },
      };
    }),
  });

  useEffect(() => {
    return () => {
      queryClient.cancelQueries(['procedures', studyId]);
    };
  }, []);

  const authoritativeProcedures = compact(flatMap(proceduresQueries, (query) => query.data));
  const authoritativeFailed = some(proceduresQueries, 'isError');
  const allPagesLoaded = !isEmpty(proceduresQueries) && proceduresQueries.every((query) => query.isSuccess);
  const lastPageIsFull = allPagesLoaded && size(last(proceduresQueries)?.data) >= PAGE_SIZE;
  const authoritativeComplete = Boolean(study) && allPagesLoaded && !lastPageIsFull;

  useEffect(() => {
    if (enabled && lastPageIsFull) {
      setExtraPages((previous) => ({ studyId, count: (previous.studyId === studyId ? previous.count : 0) + 1 }));
    }
  }, [enabled, lastPageIsFull, studyId, totalPages]);

  const placeholder =
    enabled && study && !authoritativeComplete && !authoritativeFailed
      ? selectCompleteCachedStudyProcedures(activeQueryClient, studyId, study.casesCount, study.slidesCount)
      : null;
  const procedures =
    placeholder && !authoritativeComplete && !authoritativeFailed ? placeholder : authoritativeProcedures;

  // The cached placeholder is display-only: it may hold assignments that changed without altering study counts.
  const isLoading =
    enabled &&
    !authoritativeComplete &&
    (isStatisticsLoading ||
      (!study && isStatisticsFetching) ||
      lastPageIsFull ||
      (!isEmpty(proceduresQueries) && some(proceduresQueries, (query) => query.isLoading || query.isFetching)));
  const isError = isStatisticsError || (!isStatisticsLoading && !study) || authoritativeFailed;

  return { procedures, isError, isLoading, proceduresQueries, studyCasesCount: study?.casesCount };
};
